import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import {
  User,
  GraduationCap,
  Video,
  Calendar,
  Newspaper,
  BookOpen,
  Lock,
  Award,
  Users,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Star,
  Shield,
  ChevronRight,
  Play,
  Clock,
  Eye,
  Sparkles,
  Crown,
  MessageSquare,
  TrendingUp
} from 'lucide-react';

const InvitadoDashboardPage = () => {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, [isAuthenticated]);

  const fetchDashboardData = async () => {
    try {
      const [dashboardRes, verificationRes] = await Promise.all([
        api.get('/content/free-user/dashboard'),
        api.get('/verification/status')
      ]);
      setDashboardData(dashboardRes.data);
      setVerificationStatus(verificationRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el dashboard",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  const accessData = dashboardData?.access_summary || {};
  const lockedBenefits = dashboardData?.locked_benefits || [];
  const availableContent = dashboardData?.available_content || {};

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Header */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <Badge className="bg-white/10 text-white border-white/20 mb-4">
                  <Star className="h-3 w-3 mr-1" />
                  Plan Invitado Gratuito
                </Badge>
                <h1 className="text-3xl lg:text-4xl font-bold mb-2">
                  Bienvenido, {user?.name?.split(' ')[0]}
                </h1>
                <p className="text-gray-300 text-lg">
                  Contenido estratégico curado por la Unión de Profesionales Inmobiliarios de México
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  className="bg-white text-gray-900 hover:bg-gray-100"
                  onClick={() => navigate('/afiliacion')}
                  data-testid="upgrade-btn-header"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Convertirme en Miembro
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Verification Status Banner */}
          {verificationStatus && verificationStatus.completion_percentage < 100 && (
            <Card className="mb-8 border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <Shield className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-amber-900">Completa tu verificación</h3>
                      <p className="text-sm text-amber-700">{verificationStatus.next_step}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className="text-2xl font-bold text-amber-900">{verificationStatus.completion_percentage}%</span>
                      <Progress value={verificationStatus.completion_percentage} className="w-32 h-2 mt-1" />
                    </div>
                    <Button 
                      variant="outline"
                      className="border-amber-300 text-amber-900 hover:bg-amber-100"
                      onClick={() => navigate('/registro-institucional')}
                    >
                      Completar
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content Area */}
            <div className="lg:col-span-2 space-y-8">
              {/* Access Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Video className="h-5 w-5" />
                    Tu Acceso a Master Classes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Este mes</p>
                      <p className="text-3xl font-bold text-gray-900">
                        {accessData.master_classes_remaining || 0}
                        <span className="text-lg font-normal text-gray-500"> / 1</span>
                      </p>
                      <p className="text-sm text-gray-500 mt-1">Master Class disponible</p>
                    </div>
                    {accessData.master_classes_remaining > 0 ? (
                      <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle2 className="h-8 w-8 text-green-600" />
                      </div>
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                        <Lock className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <div className="flex items-start gap-3">
                      <Sparkles className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-blue-900 font-medium">
                          Miembros activos tienen acceso ilimitado
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                          Desbloquea todas las master classes, certificaciones y rutas académicas
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Available Master Classes */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <GraduationCap className="h-5 w-5" />
                      Master Classes Disponibles
                    </CardTitle>
                    <Badge variant="outline">Acceso Curado</Badge>
                  </div>
                  <CardDescription>
                    Contenido introductorio seleccionado para tu nivel profesional
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {availableContent.master_classes?.length > 0 ? (
                    <div className="space-y-4">
                      {availableContent.master_classes.map((mc, index) => (
                        <div 
                          key={mc.id}
                          className="flex items-center gap-4 p-4 border rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/master-classes/${mc.id}`)}
                        >
                          <div className="relative w-24 h-16 rounded-lg bg-gray-200 overflow-hidden flex-shrink-0">
                            {mc.thumbnail_url ? (
                              <img src={mc.thumbnail_url} alt={mc.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Play className="h-8 w-8 text-gray-400" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                              <Play className="h-8 w-8 text-white" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 truncate">{mc.title}</h4>
                            <p className="text-sm text-gray-500">{mc.instructor}</p>
                            <Badge variant="outline" className="mt-1 text-xs">{mc.category}</Badge>
                          </div>
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Video className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No hay master classes disponibles actualmente</p>
                    </div>
                  )}
                  
                  <div className="mt-6 p-4 bg-gray-100 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Lock className="h-5 w-5 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Accede a <strong>+50 Master Classes</strong> completas
                        </span>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => navigate('/afiliacion')}>
                        Ver planes
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* News Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Newspaper className="h-5 w-5" />
                      Información Estratégica del Sector
                    </CardTitle>
                    <Link to="/noticias" className="text-sm text-gray-500 hover:text-gray-900">
                      Ver todas →
                    </Link>
                  </div>
                  <CardDescription>
                    Noticias y análisis curados por UPIM
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {availableContent.news?.length > 0 ? (
                    <div className="divide-y">
                      {availableContent.news.map((article) => (
                        <div 
                          key={article.id}
                          className="py-4 first:pt-0 last:pb-0 cursor-pointer hover:bg-gray-50 -mx-4 px-4 transition-colors"
                          onClick={() => navigate(`/noticias/${article.slug}`)}
                        >
                          <div className="flex items-start gap-3">
                            <Badge variant="outline" className="text-xs flex-shrink-0">{article.category}</Badge>
                            <div>
                              <h4 className="font-medium text-gray-900 hover:text-blue-600 transition-colors">
                                {article.title}
                              </h4>
                              {article.excerpt && (
                                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{article.excerpt}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Newspaper className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No hay noticias disponibles</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Free Webinars */}
              {availableContent.free_webinars?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Próximos Eventos Abiertos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {availableContent.free_webinars.map((webinar) => (
                        <div 
                          key={webinar.id}
                          className="flex items-center justify-between p-4 border rounded-xl"
                        >
                          <div>
                            <h4 className="font-medium text-gray-900">{webinar.title}</h4>
                            <p className="text-sm text-gray-500">{webinar.instructor}</p>
                            {webinar.date && (
                              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {new Date(webinar.date).toLocaleDateString('es-MX', { 
                                  weekday: 'long', 
                                  day: 'numeric', 
                                  month: 'long' 
                                })}
                              </p>
                            )}
                          </div>
                          <Button size="sm" onClick={() => navigate(`/webinars/${webinar.id}`)}>
                            Registrarme
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-sm text-gray-600">
                        <Lock className="h-4 w-4 inline mr-1" />
                        Acceso preferente y prioridad de registro disponible solo para <strong>miembros activos</strong>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar - Locked Benefits */}
            <div className="space-y-6">
              {/* Upgrade CTA Card */}
              <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white border-0">
                <CardContent className="p-6">
                  <div className="text-center">
                    <div className="h-16 w-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                      <Crown className="h-8 w-8" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Nivel Profesional</h3>
                    <p className="text-gray-300 text-sm mb-6">
                      {dashboardData?.upgrade_message?.headline || "El siguiente nivel desbloquea formación completa"}
                    </p>
                    <Button 
                      className="w-full bg-white text-gray-900 hover:bg-gray-100"
                      onClick={() => navigate('/afiliacion')}
                      data-testid="upgrade-btn-sidebar"
                    >
                      Conocer Membresías
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Locked Benefits */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Beneficios por Desbloquear</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {lockedBenefits.map((benefit, index) => {
                    const iconMap = {
                      graduation: GraduationCap,
                      users: Users,
                      video: Video,
                      book: BookOpen,
                      badge: Award
                    };
                    const Icon = iconMap[benefit.icon] || Lock;
                    
                    return (
                      <div key={index} className="flex items-start gap-3 group">
                        <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors">
                          <Icon className="h-5 w-5 text-gray-500" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 text-sm flex items-center gap-2">
                            {benefit.title}
                            <Lock className="h-3 w-3 text-gray-400" />
                          </h4>
                          <p className="text-xs text-gray-500 mt-0.5">{benefit.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Stats Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Tu Actividad
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Contenido consumido</span>
                      <span className="font-bold text-gray-900">{accessData.total_content_consumed || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Master Classes vistas</span>
                      <span className="font-bold text-gray-900">{accessData.master_classes_used || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Miembro desde</span>
                      <span className="text-sm text-gray-900">
                        {user?.created_at ? new Date(user.created_at).toLocaleDateString('es-MX', { month: 'short', year: 'numeric' }) : '-'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Help Card */}
              <Card className="bg-blue-50 border-blue-100">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <MessageSquare className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-blue-900 text-sm">¿Tienes dudas?</h4>
                      <p className="text-xs text-blue-700 mt-1">
                        Nuestro equipo está disponible para ayudarte
                      </p>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="mt-3 border-blue-200 text-blue-700 hover:bg-blue-100"
                        onClick={() => navigate('/contacto')}
                      >
                        Contactar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default InvitadoDashboardPage;
